import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  FiCheck,
  FiChevronRight,
  FiHeart,
  FiMinus,
  FiPlus,
  FiRefreshCw,
  FiShield,
  FiTruck,
  FiWind,
  FiX,
  FiZoomIn,
} from "react-icons/fi";
import productData from "../Datas/productData";
import siteConfig from "../Datas/siteConfig";
import "../css/productdetails.scss";
import { CartContext } from "./CartContext";

const formatPrice = (value) =>
  new Intl.NumberFormat(siteConfig.locale, {
    style: "currency",
    currency: siteConfig.currency,
  }).format(Number(value) || 0);

const colourValue = (colour) => {
  const label = colour.toLowerCase();
  const colours = {
    black: "#151718",
    charcoal: "#3e4244",
    graphite: "#4c5154",
    navy: "#15283d",
    midnight: "#14273a",
    cobalt: "#195b9d",
    blue: "#52708b",
    teal: "#3f7778",
    green: "#405b45",
    moss: "#66704e",
    olive: "#6d704b",
    khaki: "#a49369",
    brown: "#70513f",
    rust: "#a5472d",
    orange: "#ce5d25",
    coral: "#d66f59",
    berry: "#6f2f4a",
    plum: "#5b334e",
    yellow: "#e5b916",
    cream: "#e7dfcf",
    sand: "#cbbca4",
    sage: "#b7c1ad",
    grey: "#929799",
    gray: "#929799",
    slate: "#5c6872",
  };
  const name = Object.keys(colours).find((key) => label.includes(key));
  return colours[name] || "#73756f";
};

const toTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

function ProductImage({ src, alt, onClick, priority = false }) {
  const [failed, setFailed] = useState(false);

  return (
    <button
      type="button"
      className={`pdp-gallery__tile${failed ? " is-missing" : ""}`}
      onClick={onClick}
      disabled={failed}
      aria-label={`Zoom ${alt}`}
    >
      {!failed && (
        <img
          src={src}
          alt={alt}
          loading={priority ? "eager" : "lazy"}
          decoding="async"
          onError={() => setFailed(true)}
        />
      )}
      <span className="pdp-gallery__zoom" aria-hidden="true">
        <FiZoomIn />
      </span>
    </button>
  );
}

function RelatedProduct({ product }) {
  return (
    <article className="pdp-related__card">
      <Link to={`/shopDetails/${product.id}`} onClick={toTop}>
        <span className="pdp-related__media">
          <img src={product.image} alt={product.name} loading="lazy" />
          {product.badge && <small>{product.badge}</small>}
        </span>
        <span className="pdp-related__category">{product.category}</span>
        <strong>{product.name}</strong>
        <span>{formatPrice(product.price)}</span>
      </Link>
    </article>
  );
}

function ProductDetail() {
  const { id } = useParams();
  const product = productData.find((item) => item.id === Number(id));
  const cart = useContext(CartContext);
  const [selectedSize, setSelectedSize] = useState(product?.size?.[0] || "");
  const [selectedColor, setSelectedColor] = useState(
    product?.colors?.[0] || ""
  );
  const [quantity, setQuantity] = useState(1);
  const [zoomedImage, setZoomedImage] = useState("");
  const [addedMessage, setAddedMessage] = useState("");
  const lightboxCloseRef = useRef(null);
  const previousFocusRef = useRef(null);

  useEffect(() => {
    if (!product) return undefined;
    setSelectedSize(product.size?.[0] || "");
    setSelectedColor(product.colors?.[0] || "");
    setQuantity(1);
    setAddedMessage("");
    document.title = `${product.name} | ${siteConfig.brandName}`;
    window.scrollTo({ top: 0 });

    return () => {
      document.title = `${siteConfig.brandName} | Outdoor clothing & equipment`;
    };
  }, [product]);

  useEffect(() => {
    if (!zoomedImage) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const focusFrame = window.requestAnimationFrame(() => {
      lightboxCloseRef.current?.focus();
    });
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setZoomedImage("");
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
      if (previousFocusRef.current?.isConnected) {
        previousFocusRef.current.focus();
      }
    };
  }, [zoomedImage]);

  const relatedProducts = useMemo(() => {
    if (!product) return [];
    const sameCategory = productData.filter(
      (item) => item.id !== product.id && item.category === product.category
    );
    const remainder = productData.filter(
      (item) => item.id !== product.id && item.category !== product.category
    );
    return [...sameCategory, ...remainder].slice(0, 4);
  }, [product]);

  const variantCartQuantity = product
    ? cart?.cartItems?.find(
        (item) =>
          item.id === product.id &&
          item.size === selectedSize &&
          item.color === selectedColor
      )?.quantity || 0
    : 0;
  const availableToAdd = product
    ? Math.max(product.stock - variantCartQuantity, 0)
    : 0;

  useEffect(() => {
    if (!product) return;
    setQuantity((current) =>
      availableToAdd > 0 ? Math.min(current, availableToAdd) : 1
    );
  }, [availableToAdd, product]);

  if (!product) {
    return (
      <main className="pdp-not-found">
        <p className="pdp-eyebrow">404 / Product not found</p>
        <h1>This trail ends here.</h1>
        <p>
          The product may have moved, but there is plenty more gear to find.
        </p>
        <Link to="/shop" onClick={toTop}>
          Explore all products
        </Link>
      </main>
    );
  }

  const isWishlisted = cart?.isWishlisted?.(product.id) || false;
  const compareAtPrice = Number(product.compareAtPrice);
  const onSale = compareAtPrice > Number(product.price);
  const canAddToCart = product.inStock && availableToAdd > 0;

  const handleAddToCart = () => {
    if (!canAddToCart) return;
    const addedQuantity = Math.min(quantity, availableToAdd);
    cart?.addToCart(product.id, addedQuantity, selectedSize, selectedColor);
    setAddedMessage(
      `${addedQuantity} × ${product.name} in ${selectedColor}, ${selectedSize} added to your bag.`
    );
  };

  const chooseColour = (colour) => {
    setSelectedColor(colour);
    setQuantity(1);
    setAddedMessage("");
  };

  const chooseSize = (size) => {
    setSelectedSize(size);
    setQuantity(1);
    setAddedMessage("");
  };

  const openLightbox = (image) => {
    previousFocusRef.current = document.activeElement;
    setZoomedImage(image);
  };

  return (
    <main className="pdp">
      <nav className="pdp-breadcrumb" aria-label="Breadcrumb">
        <Link to="/" onClick={toTop}>
          Home
        </Link>
        <FiChevronRight aria-hidden="true" />
        <Link
          to={`/shop?category=${encodeURIComponent(product.category)}`}
          onClick={toTop}
        >
          {product.category}
        </Link>
        <FiChevronRight aria-hidden="true" />
        <span>{product.name}</span>
      </nav>

      <section className="pdp-layout" aria-labelledby="product-title">
        <div className="pdp-gallery" aria-label="Product images">
          <div className="pdp-gallery__grid">
            {product.images.map((image, index) => (
              <ProductImage
                key={image}
                src={image}
                alt={`${product.name}, view ${index + 1}`}
                priority={index === 0}
                onClick={() => openLightbox(image)}
              />
            ))}
          </div>
          <p className="pdp-gallery__count">{product.images.length} views</p>
        </div>

        <aside className="pdp-buybox">
          <div className="pdp-buybox__labels">
            {product.badge && <span>{product.badge}</span>}
            {product.tags.includes("waterproof") && <span>Waterproof</span>}
          </div>
          <p className="pdp-eyebrow">{product.activity}</p>
          <h1 id="product-title">{product.name}</h1>
          <p className="pdp-buybox__subtitle">{product.description}</p>

          <div className="pdp-price">
            <strong className={onSale ? "is-sale" : ""}>
              {formatPrice(product.price)}
            </strong>
            {onSale && <del>{formatPrice(compareAtPrice)}</del>}
            <span>Taxes shown where applicable</span>
          </div>

          <fieldset className="pdp-option pdp-option--colour">
            <legend>
              Colour <strong>{selectedColor}</strong>
            </legend>
            <div>
              {product.colors.map((colour) => (
                <button
                  type="button"
                  key={colour}
                  className={selectedColor === colour ? "is-selected" : ""}
                  aria-label={`Choose ${colour}`}
                  aria-pressed={selectedColor === colour}
                  onClick={() => chooseColour(colour)}
                >
                  <span style={{ backgroundColor: colourValue(colour) }} />
                </button>
              ))}
            </div>
          </fieldset>

          <fieldset className="pdp-option pdp-option--size">
            <legend>
              <span>Size</span>
            </legend>
            <div>
              {product.size.map((size) => (
                <button
                  type="button"
                  key={size}
                  className={selectedSize === size ? "is-selected" : ""}
                  aria-pressed={selectedSize === size}
                  onClick={() => chooseSize(size)}
                >
                  {size}
                </button>
              ))}
            </div>
          </fieldset>

          <p
            className={`pdp-stock${canAddToCart ? "" : " is-limited"}`}
            role="status"
          >
            {canAddToCart
              ? `${availableToAdd} available for this selection`
              : product.inStock
              ? "Maximum available quantity already in your basket"
              : "This product is currently out of stock"}
          </p>

          <div className="pdp-purchase">
            <div className="pdp-quantity" aria-label="Quantity">
              <button
                type="button"
                aria-label="Decrease quantity"
                disabled={quantity === 1}
                onClick={() => setQuantity((value) => Math.max(value - 1, 1))}
              >
                <FiMinus aria-hidden="true" />
              </button>
              <span aria-live="polite">{quantity}</span>
              <button
                type="button"
                aria-label="Increase quantity"
                disabled={!canAddToCart || quantity >= availableToAdd}
                onClick={() =>
                  setQuantity((value) => Math.min(value + 1, availableToAdd))
                }
              >
                <FiPlus aria-hidden="true" />
              </button>
            </div>
            <button
              type="button"
              className="pdp-add"
              disabled={!canAddToCart}
              onClick={handleAddToCart}
            >
              {!product.inStock
                ? "Out of stock"
                : availableToAdd === 0
                ? "Maximum in basket"
                : "Add to basket"}
            </button>
            <button
              type="button"
              className={`pdp-wish${isWishlisted ? " is-selected" : ""}`}
              aria-label={`${isWishlisted ? "Remove from" : "Add to"} wishlist`}
              aria-pressed={isWishlisted}
              onClick={() => cart?.toggleWishlist(product.id)}
            >
              <FiHeart aria-hidden="true" />
            </button>
          </div>

          <div className="pdp-added" aria-live="polite">
            {addedMessage && (
              <>
                <FiCheck aria-hidden="true" />
                <span>{addedMessage}</span>
                <Link to="/cartpage" onClick={toTop}>
                  View basket
                </Link>
              </>
            )}
          </div>

          <div className="pdp-delivery">
            <p>
              <FiTruck aria-hidden="true" />
              <span>
                <strong>Delivery in 3–5 working days</strong>Free shipping on
                orders over {formatPrice(siteConfig.freeShippingThreshold)}
              </span>
            </p>
            <p>
              <FiRefreshCw aria-hidden="true" />
              <span>
                <strong>Free returns</strong>Return unworn items within{" "}
                {siteConfig.returnsWindow} days
              </span>
            </p>
            <p>
              <FiShield aria-hidden="true" />
              <span>
                <strong>Trail-tested quality</strong>Expert help when you need
                it
              </span>
            </p>
          </div>
        </aside>
      </section>

      <section className="pdp-features" aria-labelledby="key-features-title">
        <div className="pdp-shell">
          <p className="pdp-eyebrow">Performance at a glance</p>
          <h2 id="key-features-title">Key features</h2>
          <div className="pdp-features__grid">
            {Object.entries(product.attributes)
              .slice(0, 4)
              .map(([label, value], index) => (
                <article key={label}>
                  {index % 2 === 0 ? (
                    <FiShield aria-hidden="true" />
                  ) : (
                    <FiWind aria-hidden="true" />
                  )}
                  <h3>{label.replace(/([a-z])([A-Z])/g, "$1 $2")}</h3>
                  <p>{value}</p>
                </article>
              ))}
          </div>
        </div>
      </section>

      <section
        className="pdp-information pdp-shell"
        aria-labelledby="product-information-title"
      >
        <div className="pdp-information__intro">
          <p className="pdp-eyebrow">
            Designed for {product.activity.toLowerCase()}
          </p>
          <h2 id="product-information-title">Product information</h2>
          <p>{product.description}</p>
        </div>
        <div className="pdp-information__accordions">
          <details open>
            <summary>Details & features</summary>
            <ul>
              {product.features.map((feature) => (
                <li key={feature}>{feature}</li>
              ))}
            </ul>
          </details>
          <details>
            <summary>Material & care</summary>
            <p>{product.material}</p>
            <p>
              Close all fasteners before washing. Do not use fabric softener.
              Follow the care label inside the product.
            </p>
          </details>
          <details id="pdp-size-guide">
            <summary>Fit & size guide</summary>
            <p>
              This style has a{" "}
              {String(product.attributes.Fit || "regular").toLowerCase()} fit.
              Choose your usual size for layering comfort.
            </p>
            <p>Available sizes: {product.size.join(", ")}.</p>
          </details>
          <details>
            <summary>Delivery & returns</summary>
            <p>
              Tracked delivery costs $9.99; timing depends on destination.
              Returns are available within {siteConfig.returnsWindow} days when
              products are unworn and have their original tags.
            </p>
          </details>
        </div>
      </section>

      <section
        className="pdp-related pdp-shell"
        aria-labelledby="related-title"
      >
        <div className="pdp-related__heading">
          <div>
            <p className="pdp-eyebrow">Complete your kit</p>
            <h2 id="related-title">You may also like</h2>
          </div>
          <Link
            to={`/shop?category=${encodeURIComponent(product.category)}`}
            onClick={toTop}
          >
            View all {product.category.toLowerCase()}
          </Link>
        </div>
        <div className="pdp-related__grid">
          {relatedProducts.map((item) => (
            <RelatedProduct key={item.id} product={item} />
          ))}
        </div>
      </section>

      {zoomedImage && (
        <div
          className="pdp-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Zoomed product image"
          onKeyDown={(event) => {
            if (event.key === "Tab") {
              event.preventDefault();
              lightboxCloseRef.current?.focus();
            }
          }}
        >
          <button
            type="button"
            tabIndex={-1}
            className="pdp-lightbox__backdrop"
            aria-label="Close image backdrop"
            onClick={() => setZoomedImage("")}
          />
          <img src={zoomedImage} alt={product.name} />
          <button
            ref={lightboxCloseRef}
            type="button"
            className="pdp-lightbox__close"
            aria-label="Close image"
            onClick={() => setZoomedImage("")}
          >
            <FiX aria-hidden="true" />
          </button>
        </div>
      )}
    </main>
  );
}

export default ProductDetail;
